import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type {
  JobSalary,
  JobRemoteOptionValue,
} from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { formatSalary } from '@xing-com/crate-jobs-helpers';
import type { JobFactsData, JobHighlights } from '@xing-com/crate-jobs-helpers';
import { spaceS } from '@xing-com/tokens';

import { Highlight } from './highlight';

const remoteOptionKey: Record<JobRemoteOptionValue, string> = {
  FULL_REMOTE: 'JOBS_REMOTE_TAG',
  PARTLY_REMOTE: 'JOBS_HYBRID_TAG',
  NON_REMOTE: 'JOBS_NON_REMOTE_TAG',
} as const;

const HighlightsWrapper = styled.ul`
  list-style: none;
  padding-inline-start: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${spaceS};
`;

type HighlightsProps = {
  facts?: JobFactsData;
  highlights: JobHighlights;
  salary?: JobSalary;
};

export const Highlights: React.FC<HighlightsProps> = ({
  facts,
  highlights,
  salary,
}) => {
  const intl = useIntl();
  const { isLoggedIn } = useLoginState();

  const { isVerifiedByXing, employmentType } = facts ?? {};

  const { remoteOption, isApplyWithoutDocuments, matchingJobAgeKey } =
    highlights;

  const showApplyWithoutDocuments = isLoggedIn && isApplyWithoutDocuments;
  const isEmpty =
    !isVerifiedByXing &&
    !employmentType &&
    !salary &&
    !remoteOption &&
    !showApplyWithoutDocuments &&
    !matchingJobAgeKey;

  return isEmpty ? null : (
    <HighlightsWrapper
      aria-label={intl.formatMessage(
        {
          id: 'JOBS_JOB_DETAILS_MAIN_DETAILS',
        },
        { variable_1: '' }
      )}
    >
      {isVerifiedByXing ? (
        <Highlight>
          <FormattedMessage id="JOBS_JOB_DETAILS_XING_VERIFIED" />
        </Highlight>
      ) : null}
      {employmentType ? (
        <Highlight>
          <AriaExtendedText
            item={{ id: employmentType.name }}
            a11yItem={{
              id: 'JOBS_JOB_DETAILS_EMPLOYMENT_TYPE_A11Y',
              values: { employment: employmentType.name },
            }}
          />
        </Highlight>
      ) : null}
      {salary ? <Highlight>{formatSalary(intl, salary)}</Highlight> : null}
      {remoteOption ? (
        <Highlight>
          <FormattedMessage id={remoteOptionKey[remoteOption]} />
        </Highlight>
      ) : null}
      {showApplyWithoutDocuments ? (
        <Highlight>
          <FormattedMessage id="JOBS_HIGHLIGHT_INSTANT_XING_APPLY" />
        </Highlight>
      ) : null}
      {matchingJobAgeKey ? (
        <Highlight data-testid="matching-job-age-highlight">
          <FormattedMessage id={matchingJobAgeKey} />
        </Highlight>
      ) : null}
    </HighlightsWrapper>
  );
};
